import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

interface ARIAContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  query: string;
  setQuery: (query: string) => void;
  history: { role: 'user' | 'aria'; content: string }[];
  isThinking: boolean;
  isListening: boolean;
  isVoiceActive: boolean;
  setIsVoiceActive: (active: boolean) => void;
  startListening: () => void;
  stopListening: () => void;
  executeCommand: (query: string) => Promise<void>;
  threadId: string | null;
}

const ARIAContext = createContext<ARIAContextType | undefined>(undefined);

export const ARIAProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [history, setHistory] = useState<{ role: 'user' | 'aria'; content: string }[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);

  const recognitionRef = useRef<any>(null);
  const synthesis = window.speechSynthesis;

  // Restore history from SessionStorage per contract
  useEffect(() => {
    const saved = sessionStorage.getItem('aria_history');
    if (saved) {
      setHistory(JSON.parse(saved));
    }
  }, []);

  // Initialize Speech Recognition
  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition && !recognitionRef.current) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => setIsListening(true);
      recognition.onend = () => setIsListening(false);
      recognition.onresult = (event: any) => {
        const transcript = Array.from(event.results)
          .map((result: any) => result[0])
          .map((result: any) => result.transcript)
          .join('');
        
        setQuery(transcript);
        if (event.results[0].isFinal) {
          executeCommand(transcript);
        }
      };

      recognitionRef.current = recognition;
    }
  }, []);

  const startListening = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.start();
      } catch (e) {
        console.error('Speech start failed', e);
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  };

  const speak = (text: string) => {
    if (!isVoiceActive) return;
    synthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.pitch = 0.9; // Deeper, more authoritative voice
    synthesis.speak(utterance);
  };

  const executeCommand = async (userQuery: string) => {
    if (!userQuery.trim()) return;
    
    const newUserMessage: { role: 'user' | 'aria'; content: string } = { role: 'user', content: userQuery };
    const updatedHistory = [...history, newUserMessage];
    setHistory(updatedHistory);
    setQuery('');
    setIsThinking(true);

    const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

    try {
      const response = await axios.post(`${apiUrl}/api/v1/aria/command`, {
        query: userQuery,
        thread_id: threadId,
        context: {
          location: window.location.pathname,
          timestamp: new Date().toISOString()
        }
      });

      const ariaMessage: { role: 'user' | 'aria'; content: string } = { 
        role: 'aria', 
        content: response.data.response 
      };
      
      const finalHistory = [...updatedHistory, ariaMessage];
      setHistory(finalHistory);
      sessionStorage.setItem('aria_history', JSON.stringify(finalHistory));
      
      if (response.data.thread_id) {
        setThreadId(response.data.thread_id);
      }

      // Voice response
      speak(response.data.response);

    } catch (error) {
      console.error('ARIA command error:', error);
      const errorMsg = 'Institutional Link Interrupted.';
      setHistory([...updatedHistory, { role: 'aria', content: errorMsg }]);
      speak(errorMsg);
    } finally {
      setIsThinking(false);
    }
  };

  return (
    <ARIAContext.Provider value={{ 
      isOpen, setIsOpen, query, setQuery, history, isThinking, isListening,
      isVoiceActive, setIsVoiceActive, startListening, stopListening, executeCommand, threadId 
    }}>
      {children}
    </ARIAContext.Provider>
  );
};

export const useARIA = () => {
  const context = useContext(ARIAContext);
  if (!context) throw new Error('useARIA must be used within ARIAProvider');
  return context;
};
